/**
 * InvoicesPage — Phase 7.
 *
 * Replaces the mock-only Invoices.tsx with real API calls via
 * ``useInvoices`` and the billing mutation hooks.
 *
 * Flows:
 *  - Tabs: All | Drafts | Issued | Partially Paid | Paid | Void
 *  - Filters: student id, date range
 *  - Row actions per status: Issue / Void / Record Payment / Open PDF
 *  - "New Invoice" modal: student + line items + dates
 *  - View modal: invoice summary + linked payments
 *
 * Three-bucket config (ADR 0002) — all status labels / colours / lists of
 * actionable statuses are pulled from ``@features/invoices/config/ui``.
 *
 * All interactive surfaces carry ``data-testid`` per ADR 0003.
 */

import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';

import { Button, Card, Dropdown, Modal, Tabs, TabPanel } from '@/components/common';
import { PageContainer } from '@/components/layout';
import { ApiError } from '@core/api/client';
import {
  fetchInvoicePdf,
  useInvoices,
  useCreateInvoice,
  useIssueInvoice,
  useVoidInvoice,
  useRecordPayment,
  usePayments,
  uploadPaymentAttachment,
  type InvoiceCreate,
  type InvoiceLineItemIn,
  type InvoiceResponse,
  type InvoicesFilters,
  type PaymentCreate,
  type PaymentMethod,
} from '@features/invoices/api/billing';
import {
  INVOICE_TABS,
  INVOICE_STATUS_OPTIONS,
  INVOICE_ISSUABLE_STATUSES,
  INVOICE_VOIDABLE_STATUSES,
  INVOICE_PAYABLE_STATUSES,
  PAYMENT_METHOD_OPTIONS,
  formatMinorAmount,
  invoiceStatusColor,
  invoiceStatusLabel,
  paymentMethodLabel,
  type InvoiceTabId,
} from '@features/invoices/config/ui';

// ---------------------------------------------------------------------------
// Styled helpers
// ---------------------------------------------------------------------------

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

const StatusCell = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['2xl']};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadingState = styled(EmptyState)``;
const ErrorState = styled(EmptyState)`
  color: #dc2626;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const Th = styled.th`
  text-align: left;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: middle;
`;

const FormGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const FormSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.sm} 0 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LineItemRow = styled.div`
  display: grid;
  grid-template-columns: 2.4fr 0.6fr 1fr 0.7fr auto;
  gap: 8px;
  align-items: end;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const TotalsBox = styled.div`
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

const TotalLine = styled.div<{ $bold?: boolean }>`
  display: flex;
  justify-content: space-between;
  font-weight: ${({ $bold, theme }) =>
    $bold ? theme.typography.fontWeights.semibold : 'normal'};
`;

const TruncatedBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: #fffbeb;
  border: 1px solid #f59e0b;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: #92400e;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function plusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
}

function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unexpected error';
}

// ---------------------------------------------------------------------------
// Create Invoice modal
// ---------------------------------------------------------------------------

interface DraftLine {
  description: string;
  quantity: string;
  unitPrice: string; // major currency in the form, converted on submit
  taxRate: string;
  packageId: string;
  subjectId: string;
}

const EMPTY_LINE: DraftLine = {
  description: '',
  quantity: '1',
  unitPrice: '',
  taxRate: '0',
  packageId: '',
  subjectId: '',
};

interface NewInvoiceFormState {
  studentId: string;
  guardianId: string;
  issueDate: string;
  dueDate: string;
  notes: string;
  lines: DraftLine[];
}

const INITIAL_INVOICE_FORM: NewInvoiceFormState = {
  studentId: '',
  guardianId: '',
  issueDate: todayIso(),
  dueDate: plusDaysIso(14),
  notes: '',
  lines: [{ ...EMPTY_LINE }],
};

function buildLineItems(lines: DraftLine[]): InvoiceLineItemIn[] {
  return lines
    .filter((l) => l.description.trim() && l.unitPrice.trim())
    .map((l) => {
      const item: InvoiceLineItemIn = {
        description: l.description.trim(),
        quantity: Math.max(1, Math.floor(Number(l.quantity) || 1)),
        // Convert major-unit input to minor units. Round to avoid float dust.
        unit_price: Math.round((Number(l.unitPrice) || 0) * 100),
        tax_rate: Math.min(0.5, Math.max(0, Number(l.taxRate) || 0)),
      };
      if (l.packageId.trim()) item.package_id = l.packageId.trim();
      if (l.subjectId.trim()) item.subject_id = l.subjectId.trim();
      return item;
    });
}

function previewTotals(lines: DraftLine[]): {
  subtotal: number;
  tax: number;
  total: number;
} {
  const items = buildLineItems(lines);
  let subtotal = 0;
  let tax = 0;
  for (const it of items) {
    const lineSub = it.quantity * it.unit_price;
    subtotal += lineSub;
    tax += Math.round(lineSub * (it.tax_rate ?? 0));
  }
  return { subtotal, tax, total: subtotal + tax };
}

interface NewInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewInvoiceModal: React.FC<NewInvoiceModalProps> = ({ isOpen, onClose }) => {
  const [form, setForm] = useState<NewInvoiceFormState>(INITIAL_INVOICE_FORM);
  const createMut = useCreateInvoice();
  const loading = createMut.isPending;

  const update = useCallback(
    <K extends keyof NewInvoiceFormState>(key: K, value: NewInvoiceFormState[K]) => {
      setForm((f) => ({ ...f, [key]: value }));
    },
    [],
  );

  const updateLine = useCallback((idx: number, patch: Partial<DraftLine>) => {
    setForm((f) => {
      const lines = f.lines.slice();
      lines[idx] = { ...lines[idx], ...patch };
      return { ...f, lines };
    });
  }, []);

  const addLine = useCallback(() => {
    setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  }, []);

  const removeLine = useCallback((idx: number) => {
    setForm((f) => {
      // Always keep at least one line so the form stays usable.
      if (f.lines.length <= 1) return f;
      return { ...f, lines: f.lines.filter((_, i) => i !== idx) };
    });
  }, []);

  const totals = useMemo(() => previewTotals(form.lines), [form.lines]);

  const reset = () => setForm(INITIAL_INVOICE_FORM);

  const submit = async () => {
    if (!form.studentId.trim()) { toast.error('Student ID is required'); return; }
    const lineItems = buildLineItems(form.lines);
    if (lineItems.length === 0) {
      toast.error('Add at least one line item with a description and price');
      return;
    }
    const payload: InvoiceCreate = {
      student_id: form.studentId.trim(),
      issue_date: form.issueDate,
      due_date: form.dueDate,
      line_items: lineItems,
      currency: 'INR',
      ...(form.guardianId.trim() ? { guardian_id: form.guardianId.trim() } : {}),
      ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
    };
    try {
      const created = await createMut.mutateAsync(payload);
      toast.success(`Draft created (${created.id})`);
      reset();
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="New Invoice (Draft)"
      size="lg"
      data-testid="candeza-invoices-create-modal"
      data-flow="invoice-create-modal"
    >
      <FormGrid>
        <FormRow>
          <FormField>
            <FormLabel htmlFor="inv-student">Student ID *</FormLabel>
            <FormInput
              id="inv-student"
              data-testid="candeza-invoices-create-student-input"
              data-flow="invoice-student-input"
              value={form.studentId}
              placeholder="Student ID"
              onChange={(e) => update('studentId', e.target.value)}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="inv-guardian">Guardian ID</FormLabel>
            <FormInput
              id="inv-guardian"
              data-testid="candeza-invoices-create-guardian-input"
              value={form.guardianId}
              placeholder="optional"
              onChange={(e) => update('guardianId', e.target.value)}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FormLabel htmlFor="inv-issue">Issue date *</FormLabel>
            <FormInput
              id="inv-issue"
              type="date"
              data-testid="candeza-invoices-create-issue-date"
              value={form.issueDate}
              onChange={(e) => update('issueDate', e.target.value)}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="inv-due">Due date *</FormLabel>
            <FormInput
              id="inv-due"
              type="date"
              data-testid="candeza-invoices-create-due-date"
              value={form.dueDate}
              onChange={(e) => update('dueDate', e.target.value)}
            />
          </FormField>
        </FormRow>

        <SectionTitle>Line items</SectionTitle>
        {form.lines.map((line, idx) => (
          <LineItemRow key={idx} data-testid={`candeza-invoices-create-line-${idx}`}>
            <FormField>
              <FormLabel htmlFor={`inv-line-desc-${idx}`}>Description</FormLabel>
              <FormInput
                id={`inv-line-desc-${idx}`}
                data-testid={`candeza-invoices-create-line-desc-${idx}`}
                value={line.description}
                placeholder="e.g. Guitar — 4 sessions"
                onChange={(e) => updateLine(idx, { description: e.target.value })}
              />
            </FormField>
            <FormField>
              <FormLabel htmlFor={`inv-line-qty-${idx}`}>Qty</FormLabel>
              <FormInput
                id={`inv-line-qty-${idx}`}
                type="number"
                min={1}
                data-testid={`candeza-invoices-create-line-qty-${idx}`}
                value={line.quantity}
                onChange={(e) => updateLine(idx, { quantity: e.target.value })}
              />
            </FormField>
            <FormField>
              <FormLabel htmlFor={`inv-line-price-${idx}`}>Unit price (₹)</FormLabel>
              <FormInput
                id={`inv-line-price-${idx}`}
                type="number"
                min={0}
                step="0.01"
                data-testid={`candeza-invoices-create-line-price-${idx}`}
                value={line.unitPrice}
                onChange={(e) => updateLine(idx, { unitPrice: e.target.value })}
              />
            </FormField>
            <FormField>
              <FormLabel htmlFor={`inv-line-tax-${idx}`}>Tax (0–0.5)</FormLabel>
              <FormInput
                id={`inv-line-tax-${idx}`}
                type="number"
                min={0}
                max={0.5}
                step="0.01"
                data-testid={`candeza-invoices-create-line-tax-${idx}`}
                value={line.taxRate}
                onChange={(e) => updateLine(idx, { taxRate: e.target.value })}
              />
            </FormField>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeLine(idx)}
              disabled={form.lines.length <= 1}
              data-testid={`candeza-invoices-create-line-remove-${idx}`}
            >
              Remove
            </Button>
          </LineItemRow>
        ))}

        <div>
          <Button
            variant="outline"
            size="sm"
            onClick={addLine}
            data-testid="candeza-invoices-create-line-add-btn"
          >
            + Add line
          </Button>
        </div>

        <TotalsBox data-testid="candeza-invoices-create-totals">
          <TotalLine>
            <span>Subtotal</span>
            <span>{formatMinorAmount(totals.subtotal)}</span>
          </TotalLine>
          <TotalLine>
            <span>Tax</span>
            <span>{formatMinorAmount(totals.tax)}</span>
          </TotalLine>
          <TotalLine $bold>
            <span>Total</span>
            <span data-testid="candeza-invoices-create-total">
              {formatMinorAmount(totals.total)}
            </span>
          </TotalLine>
        </TotalsBox>

        <FormField>
          <FormLabel htmlFor="inv-notes">Notes</FormLabel>
          <FormInput
            as="textarea"
            id="inv-notes"
            data-testid="candeza-invoices-create-notes"
            value={form.notes}
            placeholder="Internal notes (optional)"
            onChange={(e) => update('notes', (e.target as HTMLTextAreaElement).value)}
            style={{ minHeight: '60px', resize: 'vertical' }}
          />
        </FormField>
      </FormGrid>

      <ModalFooter>
        <Button
          variant="ghost"
          onClick={() => { reset(); onClose(); }}
          disabled={loading}
          data-testid="candeza-invoices-create-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          isLoading={loading}
          disabled={loading}
          data-testid="candeza-invoices-create-submit-btn"
          data-flow="invoice-submit-btn"
        >
          Save draft
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Void modal
// ---------------------------------------------------------------------------

export interface VoidInvoiceModalProps {
  invoice: InvoiceResponse;
  onClose: () => void;
}

export const VoidInvoiceModal: React.FC<VoidInvoiceModalProps> = ({ invoice, onClose }) => {
  const [reason, setReason] = useState('');
  const voidMut = useVoidInvoice();

  const submit = async () => {
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    try {
      await voidMut.mutateAsync({ id: invoice.id, body: { reason: reason.trim() } });
      toast.success('Invoice voided');
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Void invoice ${invoice.number ?? invoice.id}`}
      size="sm"
      data-testid="candeza-invoices-void-modal"
    >
      <FormGrid>
        <FormField>
          <FormLabel htmlFor="void-reason">Reason *</FormLabel>
          <FormInput
            as="textarea"
            id="void-reason"
            data-testid="candeza-invoices-void-reason"
            value={reason}
            placeholder="Why is this being voided?"
            onChange={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            style={{ minHeight: '80px', resize: 'vertical' }}
          />
        </FormField>
      </FormGrid>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={voidMut.isPending}
          data-testid="candeza-invoices-void-dismiss-btn">
          Keep invoice
        </Button>
        <Button variant="danger" onClick={submit} isLoading={voidMut.isPending}
          data-testid="candeza-invoices-void-confirm-btn">
          Void invoice
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Record payment modal
// ---------------------------------------------------------------------------

export interface RecordPaymentModalProps {
  invoice: InvoiceResponse;
  onClose: () => void;
  /** Pre-select the payment method — e.g. the office dashboard's "Record
   * Cash Payment" quick action opens this pre-filled 'cash'. Defaults to
   * 'bank_transfer' (unchanged) when omitted. */
  defaultMethod?: PaymentMethod;
}

export const RecordPaymentModal: React.FC<RecordPaymentModalProps> = ({
  invoice,
  onClose,
  defaultMethod,
}) => {
  const [amount, setAmount] = useState((invoice.amount_due / 100).toFixed(2));
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [method, setMethod] = useState<PaymentMethod>(defaultMethod ?? 'bank_transfer');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const recordMut = useRecordPayment();

  const submit = async () => {
    const minorAmount = Math.round((Number(amount) || 0) * 100);
    if (minorAmount <= 0) { toast.error('Amount must be greater than zero'); return; }
    const payload: PaymentCreate = {
      invoice_id: invoice.id,
      amount: minorAmount,
      payment_date: paymentDate,
      method,
      ...(reference.trim() ? { reference_number: reference.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    try {
      const payment = await recordMut.mutateAsync(payload);
      if (attachment) {
        setUploadingAttachment(true);
        try {
          await uploadPaymentAttachment(payment.id, attachment);
        } catch (err) {
          // Payment already recorded — surface the attachment failure
          // separately rather than rolling back a successful payment.
          toast.error(`Payment recorded, but the attachment failed to upload: ${apiErrorMessage(err)}`);
          onClose();
          return;
        } finally {
          setUploadingAttachment(false);
        }
      }
      toast.success('Payment recorded');
      onClose();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Record payment — ${invoice.number ?? invoice.id}`}
      size="md"
      data-testid="candeza-invoices-payment-modal"
      data-flow="payment-modal"
    >
      <FormGrid>
        <TotalsBox>
          <TotalLine><span>Invoice total</span><span>{formatMinorAmount(invoice.total)}</span></TotalLine>
          <TotalLine><span>Paid so far</span><span>{formatMinorAmount(invoice.amount_paid)}</span></TotalLine>
          <TotalLine $bold><span>Outstanding</span><span>{formatMinorAmount(invoice.amount_due)}</span></TotalLine>
        </TotalsBox>

        <FormRow>
          <FormField>
            <FormLabel htmlFor="pay-amount">Amount (₹) *</FormLabel>
            <FormInput
              id="pay-amount"
              type="number"
              min={0}
              step="0.01"
              data-testid="candeza-invoices-payment-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </FormField>
          <FormField>
            <FormLabel htmlFor="pay-date">Payment date *</FormLabel>
            <FormInput
              id="pay-date"
              type="date"
              data-testid="candeza-invoices-payment-date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </FormField>
        </FormRow>

        <FormField>
          <FormLabel htmlFor="pay-method">Method *</FormLabel>
          <FormSelect
            id="pay-method"
            data-testid="candeza-invoices-payment-method"
            value={method}
            onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          >
            {PAYMENT_METHOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="pay-ref">Reference number</FormLabel>
          <FormInput
            id="pay-ref"
            data-testid="candeza-invoices-payment-ref"
            value={reference}
            placeholder="UPI ref / cheque no. / txn id"
            onChange={(e) => setReference(e.target.value)}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="pay-notes">Notes</FormLabel>
          <FormInput
            as="textarea"
            id="pay-notes"
            data-testid="candeza-invoices-payment-notes"
            value={notes}
            onChange={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
            style={{ minHeight: '60px', resize: 'vertical' }}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="pay-attachment">Proof (screenshot / receipt)</FormLabel>
          <input
            id="pay-attachment"
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
            data-testid="candeza-invoices-payment-attachment"
            onChange={(e) => setAttachment(e.target.files?.[0] ?? null)}
          />
        </FormField>
      </FormGrid>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={recordMut.isPending || uploadingAttachment}
          data-testid="candeza-invoices-payment-cancel-btn">
          Cancel
        </Button>
        <Button variant="primary" onClick={submit} isLoading={recordMut.isPending || uploadingAttachment}
          data-testid="candeza-invoices-payment-submit-btn"
          data-flow="payment-submit-btn">
          Record payment
        </Button>
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// View invoice modal — summary + linked payments
// ---------------------------------------------------------------------------

interface ViewInvoiceModalProps {
  invoice: InvoiceResponse;
  onClose: () => void;
  onRecordPayment: () => void;
}

const ViewInvoiceModal: React.FC<ViewInvoiceModalProps> = ({
  invoice, onClose, onRecordPayment,
}) => {
  const { data: paymentsData } = usePayments({ invoiceId: invoice.id, limit: 50 });
  const payments = paymentsData?.items ?? [];

  const openPdf = async () => {
    try {
      const blob = await fetchInvoicePdf(invoice.id);
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      // Revoke after a short delay so the new tab has time to load the blob.
      // We can't revoke on tab-close from here; the OS will reclaim it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (!w) toast.error('Allow pop-ups to view the PDF');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Invoice ${invoice.number ?? '(draft)'}`}
      size="lg"
      data-testid="candeza-invoices-view-modal"
    >
      <FormGrid>
        <FormRow>
          <FormField>
            <FormLabel>Student</FormLabel>
            <div>{invoice.student_id}</div>
          </FormField>
          <FormField>
            <FormLabel>Status</FormLabel>
            <StatusCell>
              <StatusDot $color={invoiceStatusColor(invoice.status)} />
              {invoiceStatusLabel(invoice.status)}
            </StatusCell>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField>
            <FormLabel>Issue date</FormLabel>
            <div>{fmtDate(invoice.issue_date)}</div>
          </FormField>
          <FormField>
            <FormLabel>Due date</FormLabel>
            <div>{fmtDate(invoice.due_date)}</div>
          </FormField>
        </FormRow>

        <SectionTitle>Line items</SectionTitle>
        <Table>
          <thead>
            <tr>
              <Th>Description</Th>
              <Th>Qty</Th>
              <Th>Unit</Th>
              <Th>Tax</Th>
              <Th>Total</Th>
            </tr>
          </thead>
          <tbody>
            {invoice.line_items.map((li, idx) => (
              <tr key={idx}>
                <Td>{li.description}</Td>
                <Td>{li.quantity}</Td>
                <Td>{formatMinorAmount(li.unit_price, invoice.currency)}</Td>
                <Td>{formatMinorAmount(li.tax_amount, invoice.currency)}</Td>
                <Td>{formatMinorAmount(li.line_total, invoice.currency)}</Td>
              </tr>
            ))}
          </tbody>
        </Table>

        <TotalsBox>
          <TotalLine><span>Subtotal</span><span>{formatMinorAmount(invoice.subtotal, invoice.currency)}</span></TotalLine>
          <TotalLine><span>Tax</span><span>{formatMinorAmount(invoice.tax_total, invoice.currency)}</span></TotalLine>
          <TotalLine $bold><span>Total</span><span>{formatMinorAmount(invoice.total, invoice.currency)}</span></TotalLine>
          <TotalLine><span>Paid</span><span>{formatMinorAmount(invoice.amount_paid, invoice.currency)}</span></TotalLine>
          <TotalLine $bold><span>Outstanding</span><span>{formatMinorAmount(invoice.amount_due, invoice.currency)}</span></TotalLine>
        </TotalsBox>

        <SectionTitle>Payments</SectionTitle>
        {payments.length === 0 ? (
          <EmptyState data-testid="candeza-invoices-view-no-payments">
            No payments recorded yet.
          </EmptyState>
        ) : (
          <Table data-testid="candeza-invoices-view-payments-table">
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>Method</Th>
                <Th>Reference</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} data-testid={`candeza-invoices-view-payment-${p.id}`}>
                  <Td>{fmtDate(p.payment_date)}</Td>
                  <Td>{paymentMethodLabel(p.method)}</Td>
                  <Td>{p.reference_number || '—'}</Td>
                  <Td>{formatMinorAmount(p.amount, invoice.currency)}</Td>
                  <Td>{p.status}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {invoice.notes && (
          <FormField>
            <FormLabel>Notes</FormLabel>
            <div>{invoice.notes}</div>
          </FormField>
        )}

        {invoice.voided_reason && (
          <FormField>
            <FormLabel>Void reason</FormLabel>
            <div>{invoice.voided_reason}</div>
          </FormField>
        )}
      </FormGrid>

      <ModalFooter>
        <Button variant="ghost" onClick={onClose}
          data-testid="candeza-invoices-view-close-btn">
          Close
        </Button>
        {invoice.number && (
          <Button variant="outline" onClick={openPdf}
            data-testid="candeza-invoices-view-pdf-btn">
            Open PDF
          </Button>
        )}
        {INVOICE_PAYABLE_STATUSES.has(invoice.status) && (
          <Button variant="primary" onClick={onRecordPayment}
            data-testid="candeza-invoices-view-record-payment-btn">
            Record payment
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

interface RowActionsProps {
  invoice: InvoiceResponse;
  onView: (i: InvoiceResponse) => void;
  onVoid: (i: InvoiceResponse) => void;
  onPay: (i: InvoiceResponse) => void;
}

const RowActions: React.FC<RowActionsProps> = ({ invoice, onView, onVoid, onPay }) => {
  const issueMut = useIssueInvoice();

  const handleIssue = async () => {
    try {
      await issueMut.mutateAsync(invoice.id);
      toast.success('Invoice issued');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  return (
    <ActionRow>
      <Button variant="ghost" size="sm"
        onClick={() => onView(invoice)}
        data-testid={`candeza-invoices-row-view-btn-${invoice.id}`}>
        View
      </Button>
      {INVOICE_ISSUABLE_STATUSES.has(invoice.status) && (
        <Button variant="outline" size="sm"
          onClick={handleIssue}
          isLoading={issueMut.isPending}
          data-testid={`candeza-invoices-row-issue-btn-${invoice.id}`}>
          Issue
        </Button>
      )}
      {INVOICE_PAYABLE_STATUSES.has(invoice.status) && (
        <Button variant="primary" size="sm"
          onClick={() => onPay(invoice)}
          data-testid={`candeza-invoices-row-pay-btn-${invoice.id}`}
          data-flow="invoice-pay-btn">
          Record payment
        </Button>
      )}
      {INVOICE_VOIDABLE_STATUSES.has(invoice.status) && (
        <Button variant="danger" size="sm"
          onClick={() => onVoid(invoice)}
          data-testid={`candeza-invoices-row-void-btn-${invoice.id}`}>
          Void
        </Button>
      )}
    </ActionRow>
  );
};

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

interface InvoicesTableProps {
  items: InvoiceResponse[];
  isLoading: boolean;
  isError: boolean;
  hasMore: boolean;
  onView: (i: InvoiceResponse) => void;
  onVoid: (i: InvoiceResponse) => void;
  onPay: (i: InvoiceResponse) => void;
}

const InvoicesTable: React.FC<InvoicesTableProps> = ({
  items, isLoading, isError, hasMore, onView, onVoid, onPay,
}) => {
  if (isLoading) {
    return <LoadingState data-testid="candeza-invoices-loading">Loading invoices…</LoadingState>;
  }
  if (isError) {
    return <ErrorState data-testid="candeza-invoices-error">Failed to load invoices.</ErrorState>;
  }
  if (!items.length) {
    return <EmptyState data-testid="candeza-invoices-empty">No invoices found.</EmptyState>;
  }

  return (
    <>
      <Table data-testid="candeza-invoices-table">
        <thead>
          <tr>
            <Th>Number</Th>
            <Th>Student</Th>
            <Th>Issue</Th>
            <Th>Due</Th>
            <Th>Total</Th>
            <Th>Outstanding</Th>
            <Th>Status</Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => (
            <tr
              key={inv.id}
              data-testid={`candeza-invoices-row-${inv.id}`}
              data-status={inv.status}
            >
              <Td>{inv.number ?? <em>draft</em>}</Td>
              <Td>{inv.student_id}</Td>
              <Td>{fmtDate(inv.issue_date)}</Td>
              <Td>{fmtDate(inv.due_date)}</Td>
              <Td>{formatMinorAmount(inv.total, inv.currency)}</Td>
              <Td>{formatMinorAmount(inv.amount_due, inv.currency)}</Td>
              <Td>
                <StatusCell>
                  <StatusDot $color={invoiceStatusColor(inv.status)} />
                  {invoiceStatusLabel(inv.status)}
                </StatusCell>
              </Td>
              <Td>
                <RowActions
                  invoice={inv}
                  onView={onView}
                  onVoid={onVoid}
                  onPay={onPay}
                />
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {hasMore && (
        <TruncatedBanner data-testid="candeza-invoices-truncated-banner">
          Showing the first batch — narrow filters to see the full picture.
        </TruncatedBanner>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const InvoicesPage: React.FC = () => {
  const [tab, setTab] = useState<InvoiceTabId>('all');
  const [studentFilter, setStudentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<InvoiceResponse | null>(null);
  const [voidTarget, setVoidTarget] = useState<InvoiceResponse | null>(null);
  const [payTarget, setPayTarget] = useState<InvoiceResponse | null>(null);

  // Tab acts as a primary status filter; the dropdown narrows further
  // (only meaningful on the "All" tab). When a specific tab is active,
  // the status dropdown is ignored to keep the UI honest.
  const filters: InvoicesFilters = useMemo(() => {
    const tabStatus = tab !== 'all' ? (tab as InvoicesFilters['status']) : undefined;
    return {
      ...(tabStatus
        ? { status: tabStatus }
        : statusFilter
          ? { status: statusFilter as InvoicesFilters['status'] }
          : {}),
      ...(studentFilter.trim() ? { studentId: studentFilter.trim() } : {}),
      ...(dateFrom ? { from: dateFrom } : {}),
      ...(dateTo ? { to: dateTo } : {}),
      limit: 100,
    };
  }, [tab, studentFilter, statusFilter, dateFrom, dateTo]);

  const { data, isLoading, isError } = useInvoices(filters);

  // Keep the modal's "invoice" prop fresh after a mutation. The hook
  // refetches the list; we look the latest copy up by id so totals like
  // amount_paid / amount_due reflect the most recent payment.
  const liveViewTarget = useMemo(() => {
    if (!viewTarget) return null;
    return data?.items.find((i) => i.id === viewTarget.id) ?? viewTarget;
  }, [viewTarget, data]);

  const tabs = INVOICE_TABS.map((t) => ({ id: t.id, label: t.label }));

  const statusOptions = [
    { value: '', label: 'All statuses' },
    ...INVOICE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
  ];

  return (
    <PageContainer
      title="Invoices"
      subtitle="Manage billing, payments, and student ledger"
      data-testid="candeza-invoices-page"
      data-flow="invoices-page"
      actions={
        <Button
          variant="primary"
          onClick={() => setNewModalOpen(true)}
          data-testid="candeza-invoices-new-btn"
          data-flow="invoice-new-btn"
        >
          + New invoice
        </Button>
      }
    >
      <Card padding="lg">
        <div data-testid="candeza-invoices-tabs">
          <Tabs tabs={tabs} activeTab={tab} onChange={(t) => setTab(t as InvoiceTabId)}>
            <FiltersRow>
              <FormInput
                placeholder="Student ID"
                value={studentFilter}
                onChange={(e) => setStudentFilter(e.target.value)}
                data-testid="candeza-invoices-student-filter"
                style={{ width: 200 }}
              />
              {tab === 'all' && (
                <div>
                  <Dropdown
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    testId="candeza-invoices-status-filter"
                  />
                </div>
              )}
              <FormInput
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="candeza-invoices-date-from"
                style={{ width: 160 }}
              />
              <FormInput
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="candeza-invoices-date-to"
                style={{ width: 160 }}
              />
            </FiltersRow>

            {INVOICE_TABS.map((tabDef) => (
              <TabPanel key={tabDef.id} value={tabDef.id} activeValue={tab}>
                <InvoicesTable
                  items={data?.items ?? []}
                  isLoading={isLoading}
                  isError={isError}
                  hasMore={data?.has_more ?? false}
                  onView={setViewTarget}
                  onVoid={setVoidTarget}
                  onPay={setPayTarget}
                />
              </TabPanel>
            ))}
          </Tabs>
        </div>
      </Card>

      <NewInvoiceModal
        isOpen={newModalOpen}
        onClose={() => setNewModalOpen(false)}
      />

      {liveViewTarget && (
        <ViewInvoiceModal
          invoice={liveViewTarget}
          onClose={() => setViewTarget(null)}
          onRecordPayment={() => {
            setPayTarget(liveViewTarget);
            setViewTarget(null);
          }}
        />
      )}

      {voidTarget && (
        <VoidInvoiceModal
          invoice={voidTarget}
          onClose={() => setVoidTarget(null)}
        />
      )}

      {payTarget && (
        <RecordPaymentModal
          invoice={payTarget}
          onClose={() => setPayTarget(null)}
        />
      )}
    </PageContainer>
  );
};

